import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { AutosaveIndicator } from '@/components/AutosaveIndicator';
import { ListingStep1 } from '@/components/listings/ListingStep1';
import { ListingStep2 } from '@/components/listings/ListingStep2';
import { ListingStep3 } from '@/components/listings/ListingStep3';
import { useAutosave } from '@/hooks/useAutosave';
import { ApiClientError, type ValidationIssue } from '@/lib/api-client';
import { listingService } from '@/services/listing.service';
import type { CreateListingInput, Listing } from '@/types/listing';
import { ListingStatus } from '@/types/listing';

// Map API field paths to user-friendly names and step numbers
const FIELD_LABELS: Record<string, { label: string; step: number }> = {
  // Step 1 - Basic Info & Images
  propertyTitle: { label: 'Property Title', step: 1 },
  propertyTagline: { label: 'Property Tagline', step: 1 },
  address: { label: 'Address', step: 1 },
  city: { label: 'City', step: 1 },
  state: { label: 'State', step: 1 },
  zipCode: { label: 'Zip Code', step: 1 },
  neighborhood: { label: 'Neighborhood', step: 1 },
  isSuggested: { label: 'Mark as Suggested', step: 1 },
  isPublic: { label: 'Public', step: 1 },
  heroImage: { label: 'Hero Image', step: 1 },
  beforeAfterImages: { label: 'Before/After Images', step: 1 },
  renderingImages: { label: '3D Renderings', step: 1 },
  floorPlanImages: { label: 'Floor Plans', step: 1 },
  // Step 2 - Pricing & Renovation
  housePrice: { label: 'House Price', step: 2 },
  renovationPriceMin: { label: 'Renovation Price (Min)', step: 2 },
  renovationPriceMax: { label: 'Renovation Price (Max)', step: 2 },
  monthlyPaymentMin: { label: 'Monthly Payment (Min)', step: 2 },
  monthlyPaymentMax: { label: 'Monthly Payment (Max)', step: 2 },
  mortgageNotes: { label: 'Mortgage Notes', step: 2 },
  renovationScopeMin: { label: 'Renovation Scope (Min)', step: 2 },
  renovationScopeMax: { label: 'Renovation Scope (Max)', step: 2 },
  renovationItems: { label: 'Renovation Items', step: 2 },
  renovationOptions: { label: 'Renovation Options', step: 2 },
  detailedRenovationList: { label: 'Detailed Renovation List', step: 2 },
  timeline: { label: 'Renovation Timeline', step: 2 },
  // Step 3 - Details & Assignments
  description: { label: 'Description', step: 3 },
  locationDetails: { label: 'Location Details', step: 3 },
  propertyDetails: { label: 'Property Details', step: 3 },
  beds: { label: 'Beds', step: 3 },
  baths: { label: 'Baths', step: 3 },
  squareFeet: { label: 'Square Feet', step: 3 },
  yearBuilt: { label: 'Year Built', step: 3 },
  lotSize: { label: 'Lot Size', step: 3 },
  contractors: { label: 'Contractors', step: 3 },
  lenders: { label: 'Lenders', step: 3 },
  status: { label: 'Status', step: 3 },
};

// Get field label from path (handles nested paths like "renovationItems.0.item")
function getFieldLabel(path: string): string {
  // Handle nested paths - extract the root field
  const parts = path.split('.');
  const rootField = parts[0] || path;
  const fieldInfo = FIELD_LABELS[rootField];
  if (fieldInfo) {
    // For array fields with index, add context
    if (parts.length >= 2) {
      const indexPart = parts[1];
      if (indexPart && !Number.isNaN(Number(indexPart))) {
        const index = Number.parseInt(indexPart, 10) + 1;
        const subField = parts[2];
        return `${fieldInfo.label} #${index}${subField ? ` (${subField})` : ''}`;
      }
    }
    return fieldInfo.label;
  }
  // Fallback: convert camelCase to Title Case
  return path.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
}

// Get step number for a field path
function getFieldStep(path: string): number {
  const parts = path.split('.');
  const rootField = parts[0] || path;
  return FIELD_LABELS[rootField]?.step || 1;
}

// Format validation issues into a readable error message
function formatValidationErrors(issues: ValidationIssue[]): string {
  if (issues.length === 0) return '';

  const errorsByStep: Record<number, string[]> = { 1: [], 2: [], 3: [] };

  for (const issue of issues) {
    const step = getFieldStep(issue.path);
    const label = getFieldLabel(issue.path);
    const stepErrors = errorsByStep[step];
    if (stepErrors) {
      stepErrors.push(`• ${label}: ${issue.message}`);
    }
  }

  const parts: string[] = [];
  const stepNames = ['', 'Basic Info & Images', 'Pricing & Renovation', 'Details & Assignments'];

  for (let step = 1; step <= 3; step++) {
    const stepErrors = errorsByStep[step];
    const stepName = stepNames[step];
    if (stepErrors && stepErrors.length > 0 && stepName) {
      parts.push(`Step ${step} (${stepName}):\n${stepErrors.join('\n')}`);
    }
  }

  return parts.join('\n\n');
}

// Generate a UUID for new listings (used for organizing uploads before the listing is saved)
const generateListingId = (): string => {
  return crypto.randomUUID();
};

const STEPS = [
  { id: 1, name: 'Basic Info & Images', description: 'Property details and media' },
  { id: 2, name: 'Pricing & Renovation', description: 'Costs and renovation options' },
  { id: 3, name: 'Details & Assignments', description: 'Property specs and team' },
];

function buildListingInput(
  formData: Partial<CreateListingInput>,
  entityId: string
): CreateListingInput {
  const input: CreateListingInput = {
    uploadId: entityId,
    propertyTitle: formData.propertyTitle!,
    address: formData.address!,
    city: formData.city!,
    state: formData.state!,
    zipCode: formData.zipCode!,
    isSuggested: formData.isSuggested,
    isPublic: formData.isPublic,
    status: formData.status,
  };

  // Optional fields
  if (formData.neighborhood) input.neighborhood = formData.neighborhood;
  if (formData.propertyTagline) input.propertyTagline = formData.propertyTagline;
  if (formData.location) input.location = formData.location;
  if (formData.heroImage) input.heroImage = formData.heroImage;
  if (formData.beforeAfterImages !== undefined)
    input.beforeAfterImages = formData.beforeAfterImages;
  if (formData.renderingImages !== undefined) input.renderingImages = formData.renderingImages;
  if (formData.floorPlanImages !== undefined) input.floorPlanImages = formData.floorPlanImages;
  if (formData.housePrice !== undefined) input.housePrice = formData.housePrice;
  if (formData.renovationPriceMin !== undefined)
    input.renovationPriceMin = formData.renovationPriceMin;
  if (formData.renovationPriceMax !== undefined)
    input.renovationPriceMax = formData.renovationPriceMax;
  if (formData.monthlyPaymentMin !== undefined)
    input.monthlyPaymentMin = formData.monthlyPaymentMin;
  if (formData.monthlyPaymentMax !== undefined)
    input.monthlyPaymentMax = formData.monthlyPaymentMax;
  if (formData.mortgageNotes) input.mortgageNotes = formData.mortgageNotes;
  if (formData.renovationScopeMin !== undefined)
    input.renovationScopeMin = formData.renovationScopeMin;
  if (formData.renovationScopeMax !== undefined)
    input.renovationScopeMax = formData.renovationScopeMax;
  if (formData.renovationItems !== undefined) {
    const validRenovationItems = formData.renovationItems.filter(
      (item) => item.item && item.item.trim() !== ''
    );
    input.renovationItems = validRenovationItems;
  }
  if (formData.renovationOptions !== undefined)
    input.renovationOptions = formData.renovationOptions;
  if (formData.detailedRenovationList)
    input.detailedRenovationList = formData.detailedRenovationList;
  if (formData.timeline) input.timeline = formData.timeline;
  if (formData.description) input.description = formData.description;
  if (formData.locationDetails) input.locationDetails = formData.locationDetails;
  if (formData.propertyDetails) input.propertyDetails = formData.propertyDetails;
  if (formData.beds !== undefined) input.beds = formData.beds;
  if (formData.baths !== undefined) input.baths = formData.baths;
  if (formData.squareFeet !== undefined) input.squareFeet = formData.squareFeet;
  if (formData.yearBuilt !== undefined) input.yearBuilt = formData.yearBuilt;
  if (formData.lotSize) input.lotSize = formData.lotSize;
  if (formData.contractors !== undefined) {
    input.contractors = formData.contractors.map((c) => ({
      contractorId: c.contractorId,
      renovationEstimate: c.renovationEstimate,
      notes: c.notes,
    }));
  }
  if (formData.lenders !== undefined) {
    input.lenders = formData.lenders.map((l) => ({
      lenderId: l.lenderId,
    }));
  }

  return input;
}

const initialFormData: Partial<CreateListingInput> = {
  propertyTitle: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  neighborhood: '',
  propertyTagline: '',
  isSuggested: false,
  isPublic: true,
  heroImage: undefined,
  beforeAfterImages: [],
  renderingImages: [],
  floorPlanImages: [],
  housePrice: undefined,
  renovationPriceMin: undefined,
  renovationPriceMax: undefined,
  monthlyPaymentMin: undefined,
  monthlyPaymentMax: undefined,
  mortgageNotes: '',
  renovationScopeMin: undefined,
  renovationScopeMax: undefined,
  renovationItems: [],
  renovationOptions: [],
  detailedRenovationList: '',
  timeline: '',
  description: '',
  locationDetails: '',
  propertyDetails: '',
  beds: undefined,
  baths: undefined,
  squareFeet: undefined,
  yearBuilt: undefined,
  lotSize: '',
  contractors: [],
  lenders: [],
  status: ListingStatus.DRAFT,
};

export function ListingForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = id && id !== 'new';

  // For new listings, generate a UUID that will be used for organizing uploads
  // This ID is stable across re-renders but unique per form instance
  const [uploadId] = useState(() => generateListingId());

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<CreateListingInput>>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(!!isEditMode);
  const [error, setError] = useState<string | null>(null);
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
  const errorRef = useRef<HTMLDivElement>(null);

  // Scroll to error message when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  // For uploads, use the listing's uploadId (edit mode) or the generated one (new mode)
  const entityId = formData.uploadId || uploadId;

  const fetchListing = useCallback(async () => {
    if (!id) return;

    setIsFetching(true);
    setError(null);

    try {
      const listing: Listing = await listingService.getById(id);
      setFormData({
        uploadId: listing.uploadId,
        propertyTitle: listing.propertyTitle,
        address: listing.address,
        city: listing.city,
        state: listing.state,
        zipCode: listing.zipCode,
        neighborhood: listing.neighborhood,
        propertyTagline: listing.propertyTagline,
        slug: listing.slug,
        location: listing.location,
        isSuggested: listing.isSuggested,
        isPublic: listing.isPublic,
        heroImage: listing.heroImage,
        beforeAfterImages: listing.beforeAfterImages,
        renderingImages: listing.renderingImages,
        floorPlanImages: listing.floorPlanImages,
        housePrice: listing.housePrice,
        renovationPriceMin: listing.renovationPriceMin,
        renovationPriceMax: listing.renovationPriceMax,
        monthlyPaymentMin: listing.monthlyPaymentMin,
        monthlyPaymentMax: listing.monthlyPaymentMax,
        mortgageNotes: listing.mortgageNotes,
        renovationScopeMin: listing.renovationScopeMin,
        renovationScopeMax: listing.renovationScopeMax,
        renovationItems: listing.renovationItems,
        renovationOptions: listing.renovationOptions,
        detailedRenovationList: listing.detailedRenovationList,
        timeline: listing.timeline,
        description: listing.description,
        locationDetails: listing.locationDetails,
        propertyDetails: listing.propertyDetails,
        beds: listing.beds,
        baths: listing.baths,
        squareFeet: listing.squareFeet,
        yearBuilt: listing.yearBuilt,
        lotSize: listing.lotSize,
        contractors: listing.contractors,
        lenders: listing.lenders,
        status: listing.status,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load listing');
    } finally {
      setIsFetching(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditMode) {
      fetchListing();
    }
  }, [isEditMode, fetchListing]);

  const handleDataChange = useCallback(
    (updates: Partial<CreateListingInput>) => {
      setFormData((prev) => ({ ...prev, ...updates }));
      // Clear validation errors when user makes changes
      if (validationIssues.length > 0) {
        setValidationIssues([]);
        setError(null);
      }
    },
    [validationIssues.length]
  );

  // Autosave in edit mode
  const autosaveValidate = useCallback((data: Partial<CreateListingInput>): boolean => {
    return !!(
      data.propertyTitle?.trim() &&
      data.address?.trim() &&
      data.city?.trim() &&
      data.state?.trim() &&
      data.zipCode?.trim() &&
      /^\d{5}(-\d{4})?$/.test(data.zipCode.trim())
    );
  }, []);

  const handleAutosave = useCallback(
    async (data: Partial<CreateListingInput>) => {
      if (!id) return;
      const input = buildListingInput(data, entityId);
      await listingService.update(id, input);
    },
    [id, entityId]
  );

  const { status: autosaveStatus } = useAutosave({
    data: formData,
    onSave: handleAutosave,
    enabled: !!isEditMode && !isFetching,
    debounceMs: 2000,
    validate: autosaveValidate,
  });

  const validateStep = useCallback(
    (step: number): boolean => {
      switch (step) {
        case 1: {
          // Require basic fields
          if (!formData.propertyTitle?.trim()) {
            setError('Property title is required');
            return false;
          }
          if (!formData.address?.trim()) {
            setError('Address is required');
            return false;
          }
          if (!formData.city?.trim()) {
            setError('City is required');
            return false;
          }
          if (!formData.state?.trim()) {
            setError('State is required');
            return false;
          }
          if (!formData.zipCode?.trim()) {
            setError('Zip code is required');
            return false;
          }
          // Validate US ZIP code format (5 digits or 5+4 format)
          const zipCodePattern = /^\d{5}(-\d{4})?$/;
          if (!zipCodePattern.test(formData.zipCode.trim())) {
            setError(
              'Invalid ZIP code format. Use 5 digits (e.g., 90210) or ZIP+4 format (e.g., 90210-1234)'
            );
            return false;
          }
          return true;
        }
        case 2:
          // Pricing step - no required fields
          return true;
        case 3:
          // Details step - no required fields
          return true;
        default:
          return true;
      }
    },
    [formData]
  );

  const handleNextStep = useCallback(() => {
    setError(null);
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
    }
  }, [currentStep, validateStep]);

  const handlePrevStep = useCallback(() => {
    setError(null);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, []);

  const handleGoToStep = useCallback(
    (step: number) => {
      // Only allow going to previous steps or validated steps
      if (step < currentStep) {
        setError(null);
        setCurrentStep(step);
      } else if (step === currentStep + 1 && validateStep(currentStep)) {
        setError(null);
        setCurrentStep(step);
      }
    },
    [currentStep, validateStep]
  );

  const handleSubmit = useCallback(async () => {
    setError(null);

    // Validate all steps
    for (let step = 1; step <= STEPS.length; step++) {
      if (!validateStep(step)) {
        setCurrentStep(step);
        return;
      }
    }

    setIsLoading(true);

    try {
      const input = buildListingInput(formData, entityId);

      if (isEditMode && id) {
        await listingService.update(id, input);
      } else {
        await listingService.create(input);
      }

      navigate('/listings');
    } catch (err) {
      console.error('Error saving listing:', err);

      if (err instanceof ApiClientError) {
        if (err.hasValidationIssues()) {
          // Handle validation errors with field-level details
          setValidationIssues(err.validationIssues);
          const formattedErrors = formatValidationErrors(err.validationIssues);
          setError(formattedErrors || err.message);

          // Navigate to the first step with errors
          const firstErrorStep = Math.min(
            ...err.validationIssues.map((issue) => getFieldStep(issue.path))
          );
          if (firstErrorStep >= 1 && firstErrorStep <= 3) {
            setCurrentStep(firstErrorStep);
          }
        } else {
          // API error without validation issues
          setValidationIssues([]);
          setError(err.message || 'Failed to save listing. Please try again.');
        }
      } else {
        // Unexpected error
        setValidationIssues([]);
        setError(
          err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [formData, isEditMode, id, navigate, validateStep, entityId]);

  if (isFetching) {
    return (
      <div className="flex items-center justify-center py-[64px]">
        <div className="h-[32px] w-[32px] animate-spin rounded-full border-2 border-[#055135] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-[24px]">
      {/* Sticky Autosave Indicator */}
      {isEditMode && autosaveStatus !== 'idle' && (
        <div className="sticky top-0 z-50 flex justify-end">
          <div className="rounded-[8px] border border-[#e5e5e5] bg-white px-[12px] py-[6px] shadow-sm">
            <AutosaveIndicator status={autosaveStatus} />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-[16px]">
        <button
          type="button"
          onClick={() => navigate('/listings')}
          className="flex h-[40px] w-[40px] items-center justify-center rounded-[8px] text-[#646464] transition-colors hover:bg-[#f5f5f5] hover:text-[#181818]"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-[24px] font-semibold text-[#181818]">
            {isEditMode ? 'Edit Listing' : 'Add New Listing'}
          </h1>
          {isEditMode && formData.propertyTitle && (
            <p className="text-[14px] text-[#646464]">{formData.propertyTitle}</p>
          )}
        </div>
      </div>

      {/* Step Indicator */}
      <div className="overflow-hidden rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <nav aria-label="Progress">
          <ol className="flex items-center">
            {STEPS.map((step, stepIdx) => (
              <li
                key={step.name}
                className={`relative ${stepIdx !== STEPS.length - 1 ? 'flex-1 pr-[20px]' : ''}`}
              >
                <button
                  type="button"
                  onClick={() => handleGoToStep(step.id)}
                  className={`group flex w-full items-center ${
                    step.id <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed'
                  }`}
                  disabled={step.id > currentStep + 1}
                >
                  <span className="flex items-center">
                    <span
                      className={`flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full text-[14px] font-medium transition-colors ${
                        step.id < currentStep
                          ? 'bg-[#055135] text-white'
                          : step.id === currentStep
                            ? 'border-2 border-[#055135] bg-white text-[#055135]'
                            : 'border-2 border-[#e5e5e5] bg-white text-[#8d8c8f]'
                      }`}
                    >
                      {step.id < currentStep ? <Check size={16} /> : step.id}
                    </span>
                    <span className="ml-[12px]">
                      <span
                        className={`block text-[13px] font-medium ${
                          step.id <= currentStep ? 'text-[#181818]' : 'text-[#8d8c8f]'
                        }`}
                      >
                        {step.name}
                      </span>
                      <span className="block text-[11px] text-[#8d8c8f]">{step.description}</span>
                    </span>
                  </span>
                </button>

                {/* Connector line */}
                {stepIdx !== STEPS.length - 1 && (
                  <div
                    className={`absolute right-0 top-[18px] h-[2px] w-full transition-colors ${
                      step.id < currentStep ? 'bg-[#055135]' : 'bg-[#e5e5e5]'
                    }`}
                    style={{ left: 'calc(36px + 12px + 100px)' }}
                  />
                )}
              </li>
            ))}
          </ol>
        </nav>
      </div>

      {/* Error Message */}
      {error && (
        <div
          ref={errorRef}
          className="rounded-[8px] border border-[#fca5a5] bg-[#fef2f2] p-[16px] text-[14px] text-[#b91c1c]"
        >
          {validationIssues.length > 0 ? (
            <div className="space-y-[12px]">
              <div className="font-medium">Please fix the following validation errors:</div>
              <div className="whitespace-pre-wrap">{error}</div>
            </div>
          ) : (
            error
          )}
        </div>
      )}

      {/* Step Content */}
      <div>
        {currentStep === 1 && (
          <ListingStep1 data={formData} onChange={handleDataChange} entityId={entityId} />
        )}
        {currentStep === 2 && (
          <ListingStep2 data={formData} onChange={handleDataChange} entityId={entityId} />
        )}
        {currentStep === 3 && <ListingStep3 data={formData} onChange={handleDataChange} />}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between rounded-[12px] border border-[#e5e5e5] bg-white p-[24px]">
        <button
          type="button"
          onClick={handlePrevStep}
          disabled={currentStep === 1}
          className="flex h-[48px] items-center gap-[8px] rounded-[8px] border border-[#e5e5e5] bg-white px-[24px] text-[14px] font-medium text-[#646464] transition-colors hover:border-[#646464] hover:text-[#181818] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArrowLeft size={18} />
          Previous
        </button>

        <div className="flex items-center gap-[12px]">
          <button
            type="button"
            onClick={() => navigate('/listings')}
            className="flex h-[48px] items-center gap-[8px] rounded-[8px] border border-[#e5e5e5] bg-white px-[24px] text-[14px] font-medium text-[#646464] transition-colors hover:border-[#646464] hover:text-[#181818]"
          >
            Cancel
          </button>

          {currentStep < STEPS.length ? (
            <button
              type="button"
              onClick={handleNextStep}
              className="flex h-[48px] items-center gap-[8px] rounded-[8px] bg-[#055135] px-[24px] text-[14px] font-medium text-white transition-colors hover:bg-[#044128]"
            >
              Next
              <ArrowRight size={18} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="flex h-[48px] items-center gap-[8px] rounded-[8px] bg-[#055135] px-[24px] text-[14px] font-medium text-white transition-colors hover:bg-[#044128] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading && <Loader2 size={18} className="animate-spin" />}
              {isEditMode ? 'Update Listing' : 'Create Listing'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
